<template lang="pug">
.bq-service-layout(v-if="currentService")
    aside.bq-sidebar
        SideNav

    main.bq-main
        //- banners & warnings (shared across all service pages)
        EmailVerifyBanner

        //- Plan state banners, built in code/plan-copy.ts: one banner per project
        //- state (suspended, disabled, contained, deleted, payment failed, cancel
        //- scheduled), then the over or near limit warning and the bandwidth
        //- banner. Limit rows name what is over and where to delete it.
        template(v-for="b in banners" :key="b.id")
            .bq-banner(:class="'bq-banner--' + b.tone")
                span.material-symbols-outlined.bq-banner-icon(translate="no") {{ b.icon }}
                span.bq-banner-text
                    | {{ b.text }}
                    template(v-for="r in b.rows" :key="r.key")
                        br
                        span(translate="no") {{ r.text }}
                        template(v-if="r.action")
                            |
                            router-link.bq-link(v-if="r.action.to" :to="r.action.to") [{{ r.action.label }}]
                            a.bq-link(v-else :href="r.action.href" target="_blank" rel="noopener") [{{ r.action.label }}]
                        template(v-else-if="r.hint")
                            |  {{ r.hint }}
                    template(v-if="b.after")
                        br
                        | {{ b.after }}
                template(v-for="a in b.actions" :key="a.label")
                    |
                    router-link.bq-link(v-if="a.to" :to="a.to") [{{ a.label }}]
                    a.bq-link(v-else-if="a.href" :href="a.href" target="_blank" rel="noopener") [{{ a.label }}]
                    a.bq-link(v-else @click="runAction(a)") [{{ a.label }}]

        router-view

//- After a paid checkout for a new project (/my-projects/await-<id>): the billing
//- ticket creates the project once the payment is confirmed, so wait for it here.
.bq-await(v-else-if="awaiting")
    BunnyLoader(v-if="awaiting !== 'gave-up'" label="Setting up your project")
    h1.bq-await-title {{ awaiting === 'gave-up' ? 'Your project is not ready yet' : 'Payment received' }}
    p.bq-await-desc(v-if="awaiting === 'waiting'") Your project is being created. This page opens it as soon as it is ready.
    p.bq-await-desc(v-else-if="awaiting === 'slow'") This is taking longer than usual. You can leave this page: the project appears in your project list once it is ready.
    p.bq-await-desc(v-else) The payment went through, but the project has not appeared yet. It is added to your project list once it is ready. If it does not appear, contact support@broadwayinc.com.
    router-link.bq-cta-link(v-if="awaiting !== 'waiting'" to="/my-projects") Go to your projects

</template>

<script setup lang="ts">
import { useRoute, useRouter } from "vue-router";
import { computed, onUnmounted, ref } from "vue";
import { customer, openBillingPortal } from "@/code/user";
import { skapi } from "@/main";
import { fullScreenLoader, removeFullScreenLoader } from "@/components/jsComponents";
import {
    currentService,
    serviceList,
    pullServices,
    prefetchServiceRecords,
    prefetchServiceUsers,
} from "@/views/project-list";

import SideNav from "@/components/sideNav.vue";
import BunnyLoader from "@/components/bunnyLoader.vue";
import { clearPendingProject } from "@/views/new-project";
import EmailVerifyBanner from "@/components/emailVerifyBanner.vue";
import { projectBanners } from "@/code/plan-copy";
import { loadPlanSpecs } from "@/code/service-spec";
import type { BannerAction } from "@/code/plan-copy";

const router = useRouter();
const route = useRoute();

let serviceId = route.path.split("/")[2];

/**
 * The plan state banners, built in code/plan-copy.ts from `service.active`, the
 * suspension attributes on the row and the Stripe subscription. Driven by the
 * state itself, never by the derived `will_terminate` date, so a suspended
 * project with no date still gets its banner.
 */
const banners = computed(() => (currentService.value ? projectBanners(currentService.value) : []));

const runAction = async (a: BannerAction) => {
    if (a.run === "billing") return openBillingPage();
    if (a.run === "refresh") {
        try {
            await currentService.value?.refresh();
        } catch (err) {
            console.warn("[main] could not refresh the project", err);
        }
        currentService.value?.applySubscriptionDerived();
    }
};

// ─── waiting for a project a paid checkout is creating ───
// Every 3 s for the first 2 minutes, then every 10 s; after 15 minutes stop
// and say so (the project still lands in the list whenever it is created).
const AWAIT_FAST_MS = 3000;
const AWAIT_SLOW_MS = 10000;
const AWAIT_SLOW_AFTER_MS = 2 * 60 * 1000;
const AWAIT_GIVE_UP_MS = 15 * 60 * 1000;
const awaiting = ref<"" | "waiting" | "slow" | "gave-up">("");
let awaitTimer: ReturnType<typeof setTimeout> | null = null;
let awaitStopped = false;

async function awaitNewProject(id: string) {
    awaiting.value = "waiting";
    removeFullScreenLoader();
    const started = Date.now();
    const poll = async () => {
        if (awaitStopped) return;
        let found: any = null;
        try {
            found = await pullServices({ service_id_to_fetch: id });
            // a list entry can still be loading
            if (found instanceof Promise) found = await found;
        } catch (err) {
            console.warn("[main] could not check for the new project", err);
        }
        if (awaitStopped) return;
        if (found) {
            clearPendingProject();
            awaiting.value = "";
            serviceId = id;
            currentService.value = found;
            // The new path raises the router's full-screen overlay, and on a project
            // route the router leaves removing it to this component, whose run() does
            // not run again for the same component instance: drop it here.
            await router.replace(`/my-projects/${id}`);
            removeFullScreenLoader();
            void prefetchServiceRecords(found);
            void prefetchServiceUsers(found);
            return;
        }
        const elapsed = Date.now() - started;
        if (elapsed >= AWAIT_GIVE_UP_MS) {
            awaiting.value = "gave-up";
            return;
        }
        if (elapsed >= AWAIT_SLOW_AFTER_MS) awaiting.value = "slow";
        awaitTimer = setTimeout(poll, elapsed >= AWAIT_SLOW_AFTER_MS ? AWAIT_SLOW_MS : AWAIT_FAST_MS);
    };
    await poll();
}

onUnmounted(() => {
    awaitStopped = true;
    if (awaitTimer) clearTimeout(awaitTimer);
});

async function run() {
    currentService.value = null;
    // Plan & Usage draws the served limits; a page opened straight on a project
    // has no plan cards to fetch them, so ask here (a no-op once they are in).
    void loadPlanSpecs();

    if (serviceId.startsWith("await-")) {
        return awaitNewProject(serviceId.replace("await-", ""));
    }
    fullScreenLoader();

    let serviceInst = serviceList?.[serviceId];
    if (!serviceInst) {
        serviceInst = pullServices({ service_id_to_fetch: serviceId });
    }

    if (serviceInst instanceof Promise) {
        serviceInst = await serviceInst;
    }

    if (!serviceInst) {
        removeFullScreenLoader();
        router.push('/my-projects');
        return;
    }

    currentService.value = serviceInst;
    removeFullScreenLoader();

    // Warm the tab caches (fire-and-forget, deduped, publish-only-if-empty):
    // Database/Users then paint instantly from their module pagers.
    void prefetchServiceRecords(currentService.value);
    void prefetchServiceUsers(currentService.value);

    // // Subdomain info (only the service views need it): on VISIT, but only ONCE
    // // per service: subdInfo.srvc is set the first time a fetch settles, and the
    // // Service instance is cached across visits, so skip the re-fetch on re-entry.
    // // (The hosting view still re-fetches via getInfo() after an actual subdomain
    // // change; the internal poll's own timer guard covers in-progress fetches.)
    // if (!currentService.value?.subdInfo?.srvc) {
    //     currentService.value?.getSubdomainInfo?.();
    // }
}
run();

// The provider's customer portal; see openBillingPortal in code/user.ts.
let openBillingPage = () => openBillingPortal();
</script>

<style lang="less" scoped>
@import "@/assets/less/bq-tokens.less";

.bq-await {
    max-width: 36rem;
    margin: 0 auto;
    padding: 5rem 1.5rem;
    text-align: center;
    font-family: @mono;
    color: @ink;
}

.bq-await-title {
    font-size: clamp(1.2rem, 3vw, 1.6rem);
    font-weight: 700;
    margin: 1.5rem 0 0.75rem;
}

.bq-await-desc {
    color: @muted;
    font-size: 0.88rem;
    line-height: 1.6;
    margin: 0 0 1.5rem;
}

.bq-shell-loading {
    min-height: 50vh;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: @mono;
    color: @ink;
}

.bq-service-layout {
    display: flex;
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    background: @paper;
    color: @ink;
    font-family: @mono;
}

.bq-sidebar {
    position: sticky;
    top: calc(4rem + var(--nav-top));
    width: 212px;
    flex-shrink: 0;
    overflow-y: auto;
    align-self: flex-start;
}

.bq-main {
    flex: 1;
    padding: 3rem 3rem 3rem;
    min-width: 0;
}

/* The banners here are the shared .bq-banner from bq-shared.less: same layout,
   type and icon size as the news banner on the project list. */
.bq-link {
    &:hover {
        text-decoration: underline;
    }
}

@media (max-width: 800px) {
    .bq-sidebar {
        display: none;
    }

    .bq-main {
        padding: 1rem;
    }
}
</style>
